import React, { useState } from "react";
import api from "../lib/api";
import { Card, Button, Input, Label } from "./ui";
import VendorSelect from "./VendorSelect";
import NumberInput from "./NumberInput";
import { errMsg, fmtIDR } from "../lib/utils";
import { toast } from "sonner";
import { X, Pencil, Layers } from "lucide-react";

export const amountLockReason = (c) => {
  if (c.cost_group_id || c.is_prorata || c.is_bulk_invoice) return "Bagian dari Prorata/Bulk Invoice — ubah lewat group.";
  if (c.cost_category === "Vendor" && (c.is_settled || Number(c.paid_amount) > 0)) return "Hutang vendor sudah dibayar/settle.";
  return null;
};

// costs: 1 item = edit tunggal, >1 item = edit massal / grouping
export default function CostEditModal({ costs, vendors, onVendorsChange, groupLabels, onClose, onSaved }) {
  const single = costs.length === 1 ? costs[0] : null;
  const hasVendor = costs.some((c) => c.cost_category === "Vendor");
  const [form, setForm] = useState({
    amount: single ? String(single.amount ?? "") : "",
    description: single ? single.description || "" : "",
    vendor_name: single ? single.vendor_name || "" : "",
    group_label: single ? single.group_label || "" : "",
  });
  const [apply, setApply] = useState({ description: true, group_label: true, vendor_name: false, amount: false });
  const [busy, setBusy] = useState(false);
  const lock = single ? amountLockReason(single) : null;

  const payload = () => {
    if (single) {
      const p = { description: form.description, group_label: form.group_label };
      if (!lock) { p.amount = Number(form.amount); if (single.cost_category === "Vendor") p.vendor_name = form.vendor_name; }
      return p;
    }
    const p = { ids: costs.map((c) => c.id) };
    Object.keys(apply).forEach((k) => { if (apply[k]) p[k] = k === "amount" ? Number(form.amount) : form[k]; });
    return p;
  };

  const submit = async (e) => {
    e.preventDefault();
    const p = payload();
    if ("amount" in p && !(p.amount > 0)) return toast.error("Jumlah harus > 0");
    setBusy(true);
    try {
      if (single) {
        await api.put(`/costs/${single.id}`, p);
        toast.success("Biaya diperbarui");
      } else {
        const r = await api.post("/costs/bulk-edit", p);
        toast.success(`${r.data.updated} biaya diperbarui`);
        if (r.data.skipped.length) toast.warning(`${r.data.skipped.length} dilewati: ${r.data.skipped.slice(0, 3).map((s) => `${s.awb_number} (${s.reason})`).join("; ")}`, { duration: 8000 });
      }
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const Chk = ({ k, children }) => single ? <Label>{children}</Label> : (
    <label className="flex items-center gap-2 mb-1.5 text-[11px] uppercase tracking-[0.08em] font-bold text-slate-500 cursor-pointer">
      <input type="checkbox" data-testid={`bulk-apply-${k}`} checked={apply[k]} onChange={(e) => setApply({ ...apply, [k]: e.target.checked })} className="accent-blue-700"/>
      {children}
    </label>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="cost-edit-modal">
      <Card className="w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-heading font-bold text-xl inline-flex items-center gap-2">
            {single ? <><Pencil size={18}/> Edit Biaya — {single.awb_number}</> : <><Layers size={18}/> Edit Massal / Grouping ({costs.length} item)</>}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
        </div>
        {!single && (
          <div className="mb-3 text-xs text-slate-500 p-2 rounded bg-slate-50 border border-slate-200 max-h-20 overflow-y-auto">
            {costs.map((c) => c.awb_number).join(", ")} · total {fmtIDR(costs.reduce((s, c) => s + Number(c.amount || 0), 0))}
            <div className="mt-1">Centang field yang mau diterapkan ke semua item terpilih.</div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Chk k="group_label">Nama Grup</Chk>
            <Input data-testid="cost-edit-group" list="cost-group-labels" value={form.group_label} disabled={!single && !apply.group_label}
              onChange={(e) => setForm({ ...form, group_label: e.target.value })} placeholder="Mis. Trucking Juli Batch 1 (kosongkan = lepas grup)"/>
            <datalist id="cost-group-labels">{groupLabels.map((g) => <option key={g} value={g}/>)}</datalist>
          </div>
          <div>
            <Chk k="description">Deskripsi</Chk>
            <Input data-testid="cost-edit-description" value={form.description} disabled={!single && !apply.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Deskripsi"/>
          </div>
          {(single ? single.cost_category === "Vendor" : hasVendor) && (
            <div>
              <Chk k="vendor_name">Nama Vendor{!single && " (hanya item Vendor)"}</Chk>
              {(single ? !lock : apply.vendor_name) ? (
                <VendorSelect testid="cost-edit-vendor" value={form.vendor_name} onChange={(v) => setForm({ ...form, vendor_name: v })} vendors={vendors} onVendorsChange={onVendorsChange}/>
              ) : <Input disabled value={single ? form.vendor_name : ""} />}
            </div>
          )}
          <div>
            <Chk k="amount">Jumlah (IDR){!single && " — sama untuk tiap item"}</Chk>
            <NumberInput decimal data-testid="cost-edit-amount" value={form.amount} disabled={single ? !!lock : !apply.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}/>
            {lock && <p className="text-[11px] text-amber-700 mt-1">Jumlah & vendor terkunci: {lock}</p>}
            {!single && apply.amount && <p className="text-[11px] text-amber-700 mt-1">Item prorata/bulk invoice/sudah settle akan dilewati untuk jumlah & vendor.</p>}
          </div>
          <p className="text-[11px] text-slate-500">Perubahan jumlah/vendor ikut memperbarui jurnal biaya terkait (tidak bisa jika periode sudah ditutup).</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>Batal</Button>
            <Button data-testid="cost-edit-save" type="submit" disabled={busy}>{busy ? "Menyimpan..." : "Simpan"}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
